import { useExperimentBucket } from '@carvana/experiment';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setExperimentsLoaded } from '@/vdp/VehicleDetailsContainer/actions';
import AppContext from '~/constants/appContext';
import {
  browserCookieIdExperiments,
  experimentNames,
  userIdExperiments
} from '~/constants/vdp/experiments';
import { useUserId } from '~/context/common/UserContext';
import useExperimentsWithIdentifiers from '~/hooks/common/useExperimentsWithIdentifiers';
import { BrowserCookies } from '~/utilities';
import { logError } from '~/utilities/errorHandlers';
import useAddExperimentToStore from './useAddExperimentToStore';

/**
 * Add experiments to state and marked them as loaded once all are stored.
 * If experiments take > 10 seconds to load, produce an error but mark them as
 * loaded so the page can get on with its business.
 */
export default function ExperimentSetter() {
  const dispatch = useDispatch();
  const [forceLoad, setForceLoad] = useState(false);
  const userId = useUserId();

  const experimentsWithIdentifiers = useExperimentsWithIdentifiers({
    browserCookieId: BrowserCookies.get('BrowserCookieId') ?? '',
    browserCookieIdExperiments,
    userId,
    userIdExperiments
  });

  /* eslint-disable react-hooks/rules-of-hooks */
  const loadedExperiments = experimentNames.filter(experimentName =>
    useAddExperimentToStore(experimentName, useExperimentBucket(experimentName))
  );
  /* eslint-enable react-hooks/rules-of-hooks */

  const didLoad =
    loadedExperiments.length ===
      Object.keys(experimentsWithIdentifiers).length || forceLoad;

  const unloadedExperiments = useMemo(
    () =>
      experimentNames.filter(
        experimentName => !loadedExperiments.includes(experimentName)
      ),
    [loadedExperiments]
  );

  useEffect(() => {
    const t = window.setTimeout(() => {
      logError(new Error('Experiments failed to load'), {
        unloadedExperiments
      });
      setForceLoad(true);
    }, 10000);

    if (didLoad) {
      window.clearTimeout(t);
      dispatch(setExperimentsLoaded(AppContext.Client));
    }

    return () => window.clearTimeout(t);
  }, [didLoad, dispatch, unloadedExperiments]);

  return null;
}
